import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { type ApiKeyCreateBody, RustyApiKeys, type RustyApiKeysSpec } from './api-keys';
import type { RustyAuthSpec } from './auth';
import {
  type ReauthPurpose,
  RustyConnectedAccounts,
  type RustyConnectedAccountsSpec,
  type StepUpChallenge,
  type UserIdentitySummary,
} from './connected-accounts';
import { RustyDeposit, type RustyDepositSpec } from './deposit';
import type {
  FormIntegrationDto,
  FormIntegrationsListDto,
  IntegrationsListQueryParams,
  LinkFormIntegrationBody,
  NewIntegrationBody,
  RotateSecretResponse,
  UpdateFormIntegrationBody,
  UpdateIntegrationBody,
} from './integrations';
import type { NotificationConfig } from './notifications';
import {
  RustySecurityLog,
  type RustySecurityLogSpec,
  type SecurityLogQuery,
  type SecurityLogResponse,
} from './security-log';
import { RustySubscription, type RustySubscriptionSpec } from './subscription';
import { type ApiProps, makeUrl } from './types/api';
import type {
  AdapterCapabilities,
  ApiKeyResponse,
  ApiKeyUpdateRequest,
  BulkMessagesResponse,
  CommonQueryParams,
  CountByDayByForm,
  CustomerPortalResponse,
  DeleteEmailResponse,
  DeliveriesQuery,
  DeliveriesResponse,
  DispatchResultResponse,
  Form,
  FormAnalyticsAggregateResponse,
  FormAnalyticsResponse,
  FormRecipient,
  FormRecipientList,
  FormRecipientsQueryParams,
  FormRejectionsResponse,
  FormSummary,
  FormsQueryParams,
  HttpBulkUpdateMessages,
  HttpMarkMessagesRead,
  HttpNewForm,
  HttpNewFormRecipient,
  HttpNewVerifiedEmail,
  HttpUpdateForm,
  HttpUpdateMessage,
  IntegrationResponse,
  IntegrationsListResponse,
  MarkReadResponse,
  MessageCountByDay,
  MessageExportQueryParams,
  MessageQueryParams,
  MessagesApiResponse,
  NewDepositHttp,
  NewSubscriptionResponse,
  ProblemMuteBody,
  ProblemsQueryParams,
  ProblemsResponse,
  RecoveryCodesCount,
  RecoveryCodesResponse,
  ReplayFailedResponse,
  SingleMessageResponse,
  SubscriptionPlanConfig,
  SubscriptionResponse,
  TotpSetupResponse,
  TwoFactorSetupComplete,
  TwoFactorStatus,
  UnreadCountResponse,
  UpdateFormSpecs,
  UsageResponse,
  VerifiedEmailsResponse,
  ViewCountByDay,
} from './types/generated';
import type { ListResponse } from './types/query';
import { makeAuthHeaders } from './utils/make-auth-headers';

type RetriableConfig = InternalAxiosRequestConfig & { authRetried?: boolean };

export class RustyFormsApi {
  private baseUrl: string;
  private timeout: number;
  private client: AxiosInstance;
  private errorHandler?: (error: AxiosError) => void;
  private successHandler?: (response: AxiosResponse) => void;
  auth?: RustyAuthSpec;

  constructor({ baseUrl, timeout, auth, errorHandler, successHandler }: ApiProps) {
    if (baseUrl) {
      this.baseUrl = baseUrl;
    } else {
      this.baseUrl = 'https://api.formshive.com/v1';
    }

    console.info(`[api] => baseUrl: ${this.baseUrl}`);

    if (timeout) {
      this.timeout = timeout;
    } else {
      this.timeout = 5000;
    }

    if (auth) {
      this.auth = auth;
    } else {
      console.warn('Auth config not set');
    }

    this.errorHandler = errorHandler;
    this.successHandler = successHandler;

    this.client = axios.create({
      baseURL: this.baseUrl,
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Every `/a/` request carries the current token, refreshed first when it has expired.
    this.client.interceptors.request.use(async (config) => {
      if (config.url?.startsWith('/a/') && this.auth) {
        await this.auth.ensureFreshToken();
        config.headers.Authorization = `Bearer ${this.auth.getAccessToken()}`;
      }
      return config;
    });

    // Add response interceptor to handle errors / success generically
    this.client.interceptors.response.use(
      (response) => {
        if (this.successHandler) {
          this.successHandler(response);
        }
        return response; // Pass through successful responses
      }, // Pass through successful responses
      async (error: AxiosError) => {
        // A 401 on a signed-in request: refresh once and replay. A dead session fails the
        // refresh, which clears it and sends the user to sign-in.
        const config = error.config as RetriableConfig | undefined;
        if (
          this.auth &&
          config &&
          !config.authRetried &&
          error.response?.status === 401 &&
          config.url?.startsWith('/a/') &&
          this.auth.getSession().isLoggedIn
        ) {
          config.authRetried = true;
          const refreshed = await this.auth.refresh().then(
            () => true,
            () => false
          );
          if (refreshed) return this.client(config);
        }
        if (this.errorHandler) {
          this.errorHandler(error);
        }
        return Promise.reject(error); // Still reject so specific handlers can override
      }
    );
  }

  getAuthApi = (): RustyAuthSpec => {
    if (!this.auth) {
      throw new Error('Auth config not set');
    }
    return this.auth;
  };

  private getDepositApi = (): RustyDepositSpec => new RustyDeposit({ client: this.client });

  private getSubscriptionApi = (): RustySubscriptionSpec =>
    new RustySubscription({ client: this.client });

  private getApiKeysApi = (): RustyApiKeysSpec => new RustyApiKeys({ client: this.client });

  private getSecurityLogApi = (): RustySecurityLogSpec =>
    new RustySecurityLog({ client: this.client });

  private getConnectedAccountsApi = (): RustyConnectedAccountsSpec =>
    new RustyConnectedAccounts({ client: this.client });

  // May be expired: the request interceptor refreshes it and sets the header it sends.
  private getAccessToken = () => this.getAuthApi().getSession().accessToken ?? '';

  private getAuthHeaders = () => makeAuthHeaders(this.getAccessToken());

  // For native fetch, which skips the axios interceptors.
  private freshAuthHeaders = async () => {
    await this.getAuthApi().ensureFreshToken();
    return makeAuthHeaders(this.getAuthApi().getAccessToken());
  };

  private getAxiosConfig = () => ({ headers: this.getAuthHeaders(), timeout: this.timeout });

  private withNotification = (notification?: NotificationConfig) =>
    notification ? { ...this.getAxiosConfig(), notification } : this.getAxiosConfig();

  getAccountBalance = async (notification?: NotificationConfig) =>
    this.getDepositApi().getAccountBalance(this.getAccessToken(), notification);

  getAccountMovements = async (query: CommonQueryParams = {}) =>
    this.getDepositApi().getAccountMovements(query, this.getAccessToken());

  getDeposits = async (query: CommonQueryParams) =>
    this.getDepositApi().getDeposits(query, this.getAccessToken());

  getDeposit = async (id: string) => this.getDepositApi().getDeposit(id, this.getAccessToken());

  newDeposit = async (deposit: NewDepositHttp, notification?: NotificationConfig) =>
    this.getDepositApi().newDeposit(deposit, this.getAccessToken(), notification);

  // Subscription endpoints
  getSubscriptionPlans = async (): Promise<SubscriptionPlanConfig[]> =>
    this.getSubscriptionApi().getSubscriptionPlans();

  getCurrentSubscription = async (
    notification?: NotificationConfig
  ): Promise<SubscriptionResponse | null> =>
    this.getSubscriptionApi().getCurrentSubscription(this.getAccessToken(), notification);

  subscribeToplan = async (
    planId: string,
    notification?: NotificationConfig
  ): Promise<NewSubscriptionResponse> =>
    this.getSubscriptionApi().subscribeToplan(planId, this.getAccessToken(), notification);

  cancelSubscription = async (): Promise<unknown> =>
    this.getSubscriptionApi().cancelSubscription(this.getAccessToken());

  getSubscriptionUsage = async (notification?: NotificationConfig): Promise<UsageResponse[]> =>
    this.getSubscriptionApi().getSubscriptionUsage(this.getAccessToken(), notification);

  createCustomerPortalSession = async (
    notification?: NotificationConfig
  ): Promise<CustomerPortalResponse> =>
    this.getSubscriptionApi().createCustomerPortalSession(this.getAccessToken(), notification);

  // API Keys endpoints
  createApiKey = async (
    request: ApiKeyCreateBody,
    notification?: NotificationConfig
  ): Promise<ApiKeyResponse> =>
    this.getApiKeysApi().createApiKey(request, this.getAccessToken(), notification);

  listApiKeys = async (): Promise<ApiKeyResponse[]> =>
    this.getApiKeysApi().listApiKeys(this.getAccessToken());

  updateApiKey = async (
    id: string,
    request: ApiKeyUpdateRequest,
    notification?: NotificationConfig
  ): Promise<{ success: boolean }> =>
    this.getApiKeysApi().updateApiKey(id, request, this.getAccessToken(), notification);

  deleteApiKey = async (id: string): Promise<{ success: boolean }> =>
    this.getApiKeysApi().deleteApiKey(id, this.getAccessToken());

  // Security log (user-facing audit trail — GET /v1/a/audit)
  getSecurityLog = async (query: SecurityLogQuery = {}): Promise<SecurityLogResponse> =>
    this.getSecurityLogApi().getSecurityLog(query, this.getAccessToken());

  // Connected accounts / identity linking (/v1/a/auth/*)
  listIdentities = async (): Promise<UserIdentitySummary[]> =>
    this.getConnectedAccountsApi().listIdentities(this.getAccessToken());

  linkOidcInitiate = async (): Promise<{ auth_url: string }> =>
    this.getConnectedAccountsApi().linkOidcInitiate(this.getAccessToken());

  reauthInitiate = async (purpose: ReauthPurpose): Promise<StepUpChallenge> =>
    this.getConnectedAccountsApi().reauthInitiate(purpose, this.getAccessToken());

  reauthNostrVerify = async (
    purpose: ReauthPurpose,
    signedEvent: unknown
  ): Promise<{ step_up_token: string }> =>
    this.getConnectedAccountsApi().reauthNostrVerify(purpose, signedEvent, this.getAccessToken());

  linkCommit = async (proofToken: string, stepUpToken: string): Promise<UserIdentitySummary> =>
    this.getConnectedAccountsApi().linkCommit(proofToken, stepUpToken, this.getAccessToken());

  unlinkIdentity = async (id: string, stepUpToken: string): Promise<{ success: true }> =>
    this.getConnectedAccountsApi().unlinkIdentity(id, stepUpToken, this.getAccessToken());

  // Forms endpoints
  newForm = async (data: HttpNewForm, notification?: NotificationConfig): Promise<Form> => {
    const response = await this.client.post<Form>(
      '/a/forms',
      data,
      this.withNotification(notification)
    );
    return response.data;
  };

  /** Only present fields change; returns the merged form. The caller confirms with the undo toast. */
  updateFormPartial = async (
    id: string,
    data: HttpUpdateForm,
    notification: NotificationConfig = { onSuccess: false }
  ): Promise<Form> => {
    const response = await this.client.patch<Form>(
      `/a/forms/${id}`,
      data,
      this.withNotification(notification)
    );
    return response.data;
  };

  updateFormSpecs = async (
    id: string,
    data: UpdateFormSpecs,
    notification?: NotificationConfig
  ): Promise<void> => {
    await this.client.patch(`/a/forms/${id}/specs`, data, this.withNotification(notification));
  };

  deleteForm = async (id: string): Promise<void> => {
    await this.client.delete(`/a/forms/${id}`, this.getAxiosConfig());
  };

  getForms = async (query?: FormsQueryParams): Promise<ListResponse<Form>> => {
    const response = await this.client.get<ListResponse<Form>>(makeUrl('/a/forms', query));
    return response.data;
  };

  getForm = async (id: string): Promise<Form> => {
    const response = await this.client.get<Form>(`/a/forms/${id}`, this.getAxiosConfig());
    return response.data;
  };

  getFormsSummary = async (notification?: NotificationConfig): Promise<FormSummary[]> => {
    const response = await this.client.get<FormSummary[]>(
      '/a/forms/summary',
      this.withNotification(notification)
    );
    return response.data;
  };

  getFormRejections = async (
    formId: string,
    days = 30,
    notification?: NotificationConfig
  ): Promise<FormRejectionsResponse> => {
    const response = await this.client.get<FormRejectionsResponse>(
      makeUrl(`/a/forms/${formId}/rejections`, { days }),
      this.withNotification(notification)
    );
    return response.data;
  };

  // Email verification endpoints
  newVerifiedEmail = async (
    data: HttpNewVerifiedEmail,
    notification?: NotificationConfig
  ): Promise<void> => {
    await this.client.post('/a/emails/verify', data, this.withNotification(notification));
  };

  verifyVerifiedEmail = async (id: string, notification?: NotificationConfig): Promise<void> => {
    await this.client.post(
      '/a/emails/verify/retry',
      { verified_email_id: id },
      notification ? this.withNotification(notification) : undefined
    );
  };

  deleteVerifiedEmail = async (id: string): Promise<DeleteEmailResponse> => {
    const response = await this.client.delete<DeleteEmailResponse>(
      `/a/emails/${id}`,
      this.getAxiosConfig()
    );
    return response.data;
  };

  setAccountEmail = async (id: string, stepUpToken: string): Promise<void> => {
    await this.client.put(
      `/a/emails/${id}/set-account-email`,
      {},
      {
        ...this.withNotification({ onSuccess: false, onError: false }),
        headers: { ...this.getAuthHeaders(), 'X-Step-Up-Token': stepUpToken },
      }
    );
  };

  getVerifiedEmails = async (
    notification?: NotificationConfig
  ): Promise<VerifiedEmailsResponse> => {
    const response = await this.client.get<VerifiedEmailsResponse>(
      '/a/emails',
      this.withNotification(notification)
    );
    return response.data;
  };

  // Form recipients endpoints
  newFormRecipient = async (
    data: HttpNewFormRecipient,
    notification?: NotificationConfig
  ): Promise<FormRecipient> => {
    const response = await this.client.post<FormRecipient>(
      '/a/forms/recipients',
      data,
      this.withNotification(notification)
    );
    return response.data;
  };

  /** Finds or creates the address (sending the verification when new) and attaches it. 409 `email_owned_by_other_account`. */
  addFormRecipientByEmail = async (
    formId: string,
    email: string,
    notification?: NotificationConfig
  ): Promise<FormRecipient> => this.newFormRecipient({ form_id: formId, email }, notification);

  getFormRecipients = async (
    formId: string,
    query?: FormRecipientsQueryParams
  ): Promise<FormRecipientList> => {
    const response = await this.client.get<FormRecipientList>(
      makeUrl(`/a/forms/${formId}/recipients`, query)
    );
    return response.data;
  };

  deleteFormRecipient = async (formId: string, recipientId: string): Promise<void> => {
    await this.client.delete(`/a/forms/${formId}/recipients/${recipientId}`, this.getAxiosConfig());
  };

  // Messages endpoints
  getMessage = async (id: string): Promise<SingleMessageResponse> => {
    const response = await this.client.get<SingleMessageResponse>(
      `/a/messages/${id}`,
      this.getAxiosConfig()
    );
    return response.data;
  };

  listMessages = async (query: MessageQueryParams): Promise<MessagesApiResponse> => {
    const response = await this.client.get<MessagesApiResponse>(makeUrl('/a/messages', query));
    return response.data;
  };

  getUnreadCount = async (notification?: NotificationConfig): Promise<UnreadCountResponse> => {
    const response = await this.client.get<UnreadCountResponse>(
      '/a/messages/unread-count',
      this.withNotification(notification)
    );
    return response.data;
  };

  updateMessage = async (
    id: string,
    data: HttpUpdateMessage,
    notification?: NotificationConfig
  ): Promise<SingleMessageResponse> => {
    const response = await this.client.patch<SingleMessageResponse>(
      `/a/messages/${id}`,
      data,
      this.withNotification(notification)
    );
    return response.data;
  };

  deleteMessage = async (id: string): Promise<void> => {
    await this.client.delete(`/a/messages/${id}`, this.getAxiosConfig());
  };

  // The caller reports the outcome itself: the count only comes back in the
  // response, so the generic interceptor notification is suppressed here.
  bulkUpdateMessages = async (body: HttpBulkUpdateMessages): Promise<BulkMessagesResponse> => {
    const response = await this.client.patch<BulkMessagesResponse>(
      '/a/messages/bulk',
      body,
      this.withNotification({ onSuccess: false })
    );
    return response.data;
  };

  markMessagesRead = async (body: HttpMarkMessagesRead): Promise<MarkReadResponse> => {
    const response = await this.client.patch<MarkReadResponse>(
      '/a/messages/read',
      body,
      this.withNotification({ onSuccess: false })
    );
    return response.data;
  };

  bulkDeleteMessages = async (messageIds: string[]): Promise<BulkMessagesResponse> => {
    // axios puts a DELETE body under `data`, not as the second argument.
    const response = await this.client.delete<BulkMessagesResponse>('/a/messages/bulk', {
      ...this.getAxiosConfig(),
      data: { message_ids: messageIds },
    });
    return response.data;
  };

  /** The endpoint wants the bearer header: fetch it through downloadMessagesCsv rather than linking to it. */
  exportMessagesCsvUrl = (query: MessageExportQueryParams = {}): string =>
    makeUrl(`${this.baseUrl}/a/messages/export.csv`, query);

  downloadMessagesCsv = async (query: MessageExportQueryParams = {}): Promise<Blob> => {
    const response = await fetch(this.exportMessagesCsvUrl(query), {
      headers: await this.freshAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(`CSV export failed: ${response.status}`);
    }
    return response.blob();
  };

  getMessageCountByDay = async (params?: {
    form_ids?: string[];
    is_spam?: boolean;
    data_type?: 'views' | 'messages' | 'combined';
    days?: number;
  }): Promise<MessageCountByDay[] | ViewCountByDay[] | FormAnalyticsResponse> => {
    const response = await this.client.get(makeUrl('/a/messages/count-by-day', params));
    return response.data;
  };

  getMessagesCountByDay = async (
    query: { form_ids?: string[]; days?: number } = {}
  ): Promise<CountByDayByForm[]> => {
    const response = await this.client.get<CountByDayByForm[]>(
      makeUrl('/a/messages/count-by-day', { ...query, group_by: 'form' })
    );
    return response.data;
  };

  // Problems
  getProblems = async (
    query: ProblemsQueryParams = {},
    notification?: NotificationConfig
  ): Promise<ProblemsResponse> => {
    const response = await this.client.get<ProblemsResponse>(
      makeUrl('/a/problems', query),
      this.withNotification(notification)
    );
    return response.data;
  };

  muteProblem = async (body: ProblemMuteBody): Promise<void> => {
    await this.client.post('/a/problems/mute', body, this.withNotification({ onSuccess: false }));
  };

  unmuteProblem = async (body: ProblemMuteBody): Promise<void> => {
    await this.client.delete('/a/problems/mute', {
      ...this.withNotification({ onSuccess: false }),
      data: body,
    });
  };

  getAnalyticsAggregate = async (params?: {
    form_ids?: string[];
    is_spam?: boolean;
  }): Promise<FormAnalyticsAggregateResponse> => {
    const response = await this.client.get<FormAnalyticsAggregateResponse>(
      makeUrl('/a/analytics/aggregate', params)
    );
    return response.data;
  };

  // ---------------------------------------------------------------------
  // Integrations (outbound-integrations engine)
  //
  // Backend: backend/formshive/src/api/integrations.rs
  // The frontend components live in `src/components/Integrations`.
  // ---------------------------------------------------------------------

  newIntegration = async (
    data: NewIntegrationBody,
    notification?: NotificationConfig
  ): Promise<IntegrationResponse> => {
    const response = await this.client.post<IntegrationResponse>(
      '/a/integrations',
      data,
      notification ? this.withNotification(notification) : undefined
    );
    return response.data;
  };

  getIntegrations = async (
    query: IntegrationsListQueryParams = {}
  ): Promise<IntegrationsListResponse> => {
    const response = await this.client.get<IntegrationsListResponse>(
      makeUrl('/a/integrations', query)
    );
    return response.data;
  };

  /** Every integration of the account; the endpoint caps a page at 100. */
  getAllIntegrations = async (): Promise<IntegrationResponse[]> => {
    const all: IntegrationResponse[] = [];
    for (;;) {
      const page = await this.getIntegrations({ limit: 100, offset: all.length });
      all.push(...page.data);
      if (page.data.length === 0 || all.length >= page.total) return all;
    }
  };

  getIntegration = async (id: string): Promise<IntegrationResponse> => {
    const response = await this.client.get<IntegrationResponse>(`/a/integrations/${id}`);
    return response.data;
  };

  getIntegrationCapabilities = async (
    notification?: NotificationConfig
  ): Promise<AdapterCapabilities[]> => {
    const response = await this.client.get<AdapterCapabilities[]>(
      '/a/integrations/capabilities',
      this.withNotification(notification)
    );
    return response.data;
  };

  updateIntegration = async (
    id: string,
    data: UpdateIntegrationBody,
    notification?: NotificationConfig
  ): Promise<IntegrationResponse> => {
    const response = await this.client.patch<IntegrationResponse>(
      `/a/integrations/${id}`,
      data,
      this.withNotification(notification)
    );
    return response.data;
  };

  deleteIntegration = async (id: string): Promise<void> => {
    await this.client.delete(`/a/integrations/${id}`, this.getAxiosConfig());
  };

  rotateIntegrationSecret = async (
    id: string,
    notification?: NotificationConfig
  ): Promise<RotateSecretResponse> => {
    const response = await this.client.post<RotateSecretResponse>(
      `/a/integrations/${id}/rotate-secret`,
      {},
      this.withNotification(notification)
    );
    return response.data;
  };

  testIntegration = async (
    id: string,
    eventType?: string,
    notification?: NotificationConfig
  ): Promise<DispatchResultResponse> => {
    const response = await this.client.post<DispatchResultResponse>(
      `/a/integrations/${id}/test`,
      { event_type: eventType },
      this.withNotification(notification)
    );
    return response.data;
  };

  listIntegrationDeliveries = async (
    id: string,
    query: DeliveriesQuery = {}
  ): Promise<DeliveriesResponse> => {
    const response = await this.client.get<DeliveriesResponse>(
      makeUrl(`/a/integrations/${id}/deliveries`, query)
    );
    return response.data;
  };

  /** Replays the latest attempt of up to 50 failed events, optionally only those since `since`. */
  replayFailedDeliveries = async (id: string, since?: string): Promise<ReplayFailedResponse> => {
    const response = await this.client.post<ReplayFailedResponse>(
      makeUrl(`/a/integrations/${id}/deliveries/replay-failed`, { since }),
      {},
      this.withNotification({ onSuccess: false })
    );
    return response.data;
  };

  replayIntegrationDelivery = async (
    deliveryId: string,
    notification?: NotificationConfig
  ): Promise<DispatchResultResponse> => {
    const response = await this.client.post<DispatchResultResponse>(
      `/a/integrations/deliveries/${deliveryId}/replay`,
      {},
      this.withNotification(notification)
    );
    return response.data;
  };

  // --- Form ⇄ integration links --------------------------------------------

  linkFormIntegration = async (
    formId: string,
    body: LinkFormIntegrationBody,
    notification?: NotificationConfig
  ): Promise<FormIntegrationDto> => {
    const response = await this.client.post<FormIntegrationDto>(
      `/a/forms/${formId}/integrations`,
      body,
      this.withNotification(notification)
    );
    return response.data;
  };

  listFormIntegrations = async (formId: string): Promise<FormIntegrationsListDto> => {
    const response = await this.client.get<FormIntegrationsListDto>(
      `/a/forms/${formId}/integrations`
    );
    return response.data;
  };

  updateFormIntegration = async (
    formId: string,
    integrationId: string,
    body: UpdateFormIntegrationBody,
    notification?: NotificationConfig
  ): Promise<FormIntegrationDto> => {
    const response = await this.client.patch<FormIntegrationDto>(
      `/a/forms/${formId}/integrations/${integrationId}`,
      body,
      this.withNotification(notification)
    );
    return response.data;
  };

  unlinkFormIntegration = async (formId: string, integrationId: string): Promise<void> => {
    await this.client.delete(
      `/a/forms/${formId}/integrations/${integrationId}`,
      this.getAxiosConfig()
    );
  };

  /** The file's signed storage URL; asked for as JSON so the browser does not follow a cross-origin redirect. */
  async downloadFile(fileId: string): Promise<string> {
    const response = await fetch(makeUrl(`${this.baseUrl}/a/files/download/${fileId}`), {
      method: 'GET',
      headers: { ...(await this.freshAuthHeaders()), Accept: 'application/json' },
    });
    if (response.ok) {
      const body = (await response.json().catch(() => null)) as { url?: unknown } | null;
      if (typeof body?.url === 'string' && body.url) return body.url;
    }
    throw new Error('File not found');
  }

  // Two-Factor Authentication endpoints
  getTwoFactorStatus = async (): Promise<TwoFactorStatus> => {
    const response = await this.client.get<TwoFactorStatus>('/a/2fa/status');
    return response.data;
  };

  setupTotp = async (
    name?: string,
    notification?: NotificationConfig
  ): Promise<TotpSetupResponse> => {
    const response = await this.client.post<TotpSetupResponse>(
      '/a/2fa/setup/totp',
      { name: name || 'Authenticator' },
      this.withNotification(notification)
    );
    return response.data;
  };

  verifyTotpSetup = async (
    setupToken: string,
    code: string,
    notification?: NotificationConfig
  ): Promise<TwoFactorSetupComplete> => {
    const response = await this.client.post<TwoFactorSetupComplete>(
      '/a/2fa/setup/totp/verify',
      { setup_token: setupToken, code },
      this.withNotification(notification)
    );
    return response.data;
  };

  disableTwoFactorMethod = async (
    methodId: string,
    code: string,
    notification?: NotificationConfig
  ): Promise<void> => {
    await this.client.delete(`/a/2fa/methods/${methodId}`, {
      ...this.withNotification(notification),
      data: { code },
    });
  };

  getRecoveryCodesCount = async (): Promise<RecoveryCodesCount> => {
    const response = await this.client.get<RecoveryCodesCount>('/a/2fa/recovery-codes');
    return response.data;
  };

  regenerateRecoveryCodes = async (
    code: string,
    notification?: NotificationConfig
  ): Promise<RecoveryCodesResponse> => {
    const response = await this.client.post<RecoveryCodesResponse>(
      '/a/2fa/recovery-codes/regenerate',
      { code },
      this.withNotification(notification)
    );
    return response.data;
  };

  // Auto-response test
  sendAutoResponseTest = async (
    formId: string,
    email: string,
    notification?: NotificationConfig
  ): Promise<void> => {
    await this.client.post(
      `/a/forms/${formId}/auto-response/test`,
      { email },
      this.withNotification(notification)
    );
  };

  // Account data export & deletion
  exportAccountData = async (): Promise<Blob> => {
    const response = await this.client.get<Blob>('/a/account/export', {
      responseType: 'blob',
      timeout: 0,
    });
    return response.data;
  };

  deleteAccount = async (
    confirmation: string,
    stepUpToken: string
  ): Promise<{ deleted: boolean }> =>
    this.getConnectedAccountsApi().deleteAccount(confirmation, stepUpToken, this.getAccessToken());

  // Chat assistant (uses native fetch for SSE streaming)
  formChat = async (
    formId: string,
    message: string,
    formState: Record<string, unknown>,
    history: { question: string; answer: string }[],
    onChunk: (content: string) => void,
    onDone: () => void,
    onError: (error: string) => void
  ): Promise<void> => {
    const url = `${this.baseUrl}/a/forms/${formId}/chat`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.freshAuthHeaders()),
        },
        body: JSON.stringify({ message, form_state: formState, history }),
      });

      if (!response.ok) {
        if (response.status === 429) {
          onError('rateLimit');
          return;
        }
        onError('error');
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        onError('error');
        return;
      }

      const decoder = new TextDecoder();
      // A read boundary can land mid-line, so hold the trailing partial event
      // back until the rest of it arrives — otherwise both halves are dropped.
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const data = trimmed.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            const content = parsed?.choices?.[0]?.delta?.content;
            if (content) {
              onChunk(content);
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }

      onDone();
    } catch {
      onError('error');
    }
  };
}
